import React from 'react';
import styled from 'styled-components';
import { LogOut, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUI } from '../../contexts/UIContext';
import { DealerUser } from '../../types';

const HeaderContainer = styled.header`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  h1 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
  }
  
  .logo-icon {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #ed2024 0%, #a01519 100%);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 1rem;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f9fafb;
  
  .user-avatar {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #ed2024 0%, #a01519 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }
  
  .user-details {
    display: flex;
    flex-direction: column;
    
    .user-name {
      font-weight: 600;
      color: #1f2937;
      font-size: 0.9rem;
    }
    
    .user-role {
      font-size: 0.75rem;
      color: #6b7280;
      text-transform: capitalize;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  &.logout {
    color: #dc2626;
    border-color: #fca5a5;
    
    &:hover {
      background: #fef2f2;
      border-color: #f87171;
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { isIframeMode } = useUI();

  const handleLogout = async () => {
    await logout();
  };

  const getUserDisplayName = () => {
    if (user?.userType === 'dealer') {
      const dealerUser = user as DealerUser;
      return `${dealerUser.firstName} ${dealerUser.lastName}`;
    }
    return user?.email || '';
  };

  const getUserRole = () => {
    if (user?.userType === 'dealer') {
      const dealerUser = user as DealerUser;
      return `${dealerUser.role.replace('_', ' ')} • ${dealerUser.dealerName}`;
    }
    return user?.role.replace('_', ' ') || '';
  };

  return (
    <HeaderContainer>
      <Logo>
        <div className="logo-icon">TF</div>
        <h1>{user?.userType === 'dealer' ? 'TimeForge' : 'TimeForge'}</h1>
      </Logo>
      
      <UserSection>
        <UserInfo>
          <div className="user-avatar">
            <User size={18} />
          </div>
          <div className="user-details">
            <span className="user-name">{getUserDisplayName()}</span>
            <span className="user-role">{getUserRole()}</span>
          </div>
        </UserInfo>
        
        {/* Hide logout button in iframe mode */}
        {!isIframeMode && (
          <ActionButtons>
            <ActionButton className="logout" onClick={handleLogout}>
              <LogOut />
              Logout
            </ActionButton>
          </ActionButtons>
        )}
      </UserSection>
    </HeaderContainer>
  );
};

export default Header;